<h2 mat-dialog-title class="m-0">
    <mat-icon class="mr-2">policy</mat-icon>
    {{ isEditMode ? 'Edit Policy' : 'Policies' }}

    <!-- Policy Explorer button -->
    <button mat-icon-button 
            class="explorer-button ml-2" 
            (click)="openPolicyExplorer()" 
            matTooltip="Open Policy Explorer - View policies by status, category, or target">
        <mat-icon>account_tree</mat-icon>
    </button>

    <!-- Close button. -->
    <button mat-icon-button mat-dialog-close class="close-button">
        <mat-icon>close</mat-icon>
    </button>
</h2>

<mat-dialog-content class="h-100-70 w-100">

    <mat-tab-group class="w-100" [(selectedIndex)]="tabIndex">
        <!-- Create/Edit Policy Tab -->
        <mat-tab [label]="isEditMode ? 'Edit Policy' : 'New Policy'">

            <div class="view-container m-3" [class.has-deleted-banner]="isEditingDeletedPolicy">
                <div class="view-header">
                    <span class="viewer-title-spacer">
                        {{ isEditMode ? 'Edit Policy (v' + form.get('version')?.value + ')' : 'Create New Policy' }}
                        <!-- Status Badge - show Draft when editing for new version -->
                        <span *ngIf="isEditMode && editingPolicy" 
                              class="status-badge ml-3"
                              [ngClass]="isEditingForNewVersion ? 'status-draft' : 'status-' + (editingPolicy.status || 'draft')">
                            <mat-icon>{{ isEditingForNewVersion ? 'edit' : getStatusConfig(editingPolicy.status).icon }}</mat-icon>
                            {{ isEditingForNewVersion ? 'Draft (New Version)' : getStatusConfig(editingPolicy.status).label }}
                        </span>
                    </span>
                    <div class="header-actions">
                        <!-- Cancel button - always show in edit mode -->
                        <button mat-button (click)="cancelEdit()" *ngIf="isEditMode">
                            <mat-icon>close</mat-icon> Cancel
                        </button>
                        
                        <!-- DRAFT: Save as Draft + Submit for Approval -->
                        <ng-container *ngIf="!isEditMode || editingPolicy?.status === 'draft'">
                            <button mat-raised-button (click)="savePolicy()" color="primary" 
                                    [disabled]="form.invalid || form.pristine">
                                <mat-icon>save</mat-icon> Save as Draft
                            </button>
                            <button mat-raised-button (click)="submitForApproval()" color="accent" 
                                    [disabled]="form.invalid">
                                <mat-icon>send</mat-icon> Submit for Approval
                            </button>
                        </ng-container>
                        
                        <!-- PENDING APPROVAL: Save Changes (keeps pending status) -->
                        <ng-container *ngIf="editingPolicy?.status === 'pending_approval'">
                            <button mat-raised-button (click)="savePolicy()" color="primary" 
                                    [disabled]="form.invalid || form.pristine">
                                <mat-icon>save</mat-icon> Save Changes
                            </button>
                        </ng-container>
                        
                        <!-- APPROVED: Save Changes + Publish Now -->
                        <ng-container *ngIf="editingPolicy?.status === 'approved'">
                            <button mat-raised-button (click)="savePolicy()" color="primary" 
                                    [disabled]="form.invalid || form.pristine">
                                <mat-icon>save</mat-icon> Save Changes
                            </button>
                            <button mat-raised-button (click)="publishPolicy()" color="accent">
                                <mat-icon>publish</mat-icon> Publish Now
                            </button>
                        </ng-container>
                        
                        <!-- SCHEDULED: Save Changes + Publish Now -->
                        <ng-container *ngIf="editingPolicy?.status === 'scheduled'">
                            <button mat-raised-button (click)="savePolicy()" color="primary" 
                                    [disabled]="form.invalid || form.pristine">
                                <mat-icon>save</mat-icon> Save Changes
                            </button>
                            <button mat-raised-button (click)="publishPolicy()" color="accent">
                                <mat-icon>publish</mat-icon> Publish Now
                            </button>
                        </ng-container>
                        
                        <!-- PUBLISHED: Update Expiry Date + Create New Draft Version -->
                        <ng-container *ngIf="editingPolicy?.status === 'published'">
                            <!-- When editing for new version, show Save New Version -->
                            <ng-container *ngIf="isEditingForNewVersion; else publishedViewMode">
                                <button type="button" mat-raised-button (click)="saveNewVersion()" color="accent"
                                        [disabled]="form.invalid"
                                        matTooltip="Save as new draft version">
                                    <mat-icon>save</mat-icon> Save New Version
                                </button>
                            </ng-container>
                            
                            <!-- Normal published view mode -->
                            <ng-template #publishedViewMode>
                                <button type="button" mat-raised-button (click)="updatePublishedExpiry()" color="accent"
                                        [disabled]="!hasExpiryDateChanged()"
                                        matTooltip="Save expiry date changes to the published policy">
                                    <mat-icon>update</mat-icon> Update Expiry Date
                                </button>
                                <button type="button" mat-raised-button (click)="createNewVersion()" color="primary"
                                        matTooltip="Edit all fields to create a new draft version">
                                    <mat-icon>add_circle</mat-icon> Create New Version
                                </button>
                            </ng-template>
                        </ng-container>
                        
                        <!-- EXPIRED: Create New Version + Republish -->
                        <ng-container *ngIf="editingPolicy?.status === 'expired'">
                            <button mat-raised-button (click)="createNewVersion()" color="primary" 
                                    [disabled]="form.invalid || form.pristine">
                                <mat-icon>add_circle</mat-icon> Create New Version
                            </button>
                            <button mat-raised-button (click)="republishPolicy()" color="accent">
                                <mat-icon>replay</mat-icon> Republish
                            </button>
                        </ng-container>
                        
                        <!-- ARCHIVED: Create New Version -->
                        <ng-container *ngIf="editingPolicy?.status === 'archived'">
                            <button mat-raised-button (click)="createNewVersion()" color="primary" 
                                    [disabled]="form.invalid || form.pristine">
                                <mat-icon>add_circle</mat-icon> Create New Version
                            </button>
                        </ng-container>
                        
                        <!-- DELETED: Reinstate & Save -->
                        <ng-container *ngIf="editingPolicy?.status === 'deleted'">
                            <button mat-raised-button (click)="reinstateAndSave()" color="primary" 
                                    [disabled]="form.invalid">
                                <mat-icon>restore</mat-icon> Reinstate & Save
                            </button>
                            <button mat-raised-button (click)="createNewVersion()" color="accent" 
                                    [disabled]="form.invalid || form.pristine">
                                <mat-icon>add_circle</mat-icon> Create New Version
                            </button>
                        </ng-container>
                        
                        <!-- CANCELLED: Reinstate or Create New Version -->
                        <ng-container *ngIf="editingPolicy?.status === 'cancelled'">
                            <button mat-raised-button (click)="reinstateCancelledOrRevoked()" color="primary">
                                <mat-icon>restore</mat-icon> Reinstate to Draft
                            </button>
                            <button mat-raised-button (click)="createNewVersion()" color="accent" 
                                    [disabled]="form.invalid || form.pristine">
                                <mat-icon>add_circle</mat-icon> Create New Version
                            </button>
                        </ng-container>
                        
                        <!-- REVOKED: Reinstate or Create New Version -->
                        <ng-container *ngIf="editingPolicy?.status === 'revoked'">
                            <button mat-raised-button (click)="reinstateCancelledOrRevoked()" color="primary">
                                <mat-icon>restore</mat-icon> Reinstate to Draft
                            </button>
                            <button mat-raised-button (click)="createNewVersion()" color="accent" 
                                    [disabled]="form.invalid || form.pristine">
                                <mat-icon>add_circle</mat-icon> Create New Version
                            </button>
                        </ng-container>
                    </div>
                </div>

                <!-- Deleted Policy Warning Banner -->
                <div class="deleted-policy-banner" *ngIf="isEditingDeletedPolicy">
                    <mat-icon>warning</mat-icon>
                    <div class="banner-content">
                        <strong>This policy has been deleted</strong>
                        <p>Make any edits needed, then click "Reinstate & Save" to restore it as a draft. Or click Cancel to leave it deleted.</p>
                    </div>
                </div>
            
                <div class="view-content policy-form">
                    <form [formGroup]="form" class="w-100">
                        
                        <!-- === Section 1: Basic Information === -->
                        <div class="form-section">
                            <h3 class="section-title">
                                <mat-icon>info</mat-icon> Basic Information
                            </h3>
                            
                            <!-- Published policy notice - only show when not editing for new version -->
                            <div class="published-edit-notice" *ngIf="shouldLockFields">
                                <mat-icon>info</mat-icon>
                                <span>This policy is published. Only the <strong>expiry date</strong> and <strong>acknowledgment deadline</strong> can be updated. To make other changes, click "Create New Version".</span>
                            </div>
                            
                            <!-- New version editing notice -->
                            <div class="published-edit-notice" *ngIf="isEditingForNewVersion" style="background: rgba(76, 175, 80, 0.1); border-color: #4caf50;">
                                <mat-icon style="color: #4caf50;">edit_note</mat-icon>
                                <span>Editing new version (v{{ form.get('version')?.value }}). All fields are editable. Click <strong>Save New Version</strong> when done, or <strong>Cancel</strong> to discard changes.</span>
                            </div>

                            <div class="form-row">
                                <mat-form-field appearance="outline" class="flex-2">
                                    <mat-label>Policy Name</mat-label>
                                    <input #policyNameInput matInput type="text" formControlName="name" placeholder="e.g., Remote Work Policy" cdkFocusInitial />
                                    <mat-icon matSuffix>title</mat-icon>
                                    <mat-hint>A clear, descriptive name for the policy</mat-hint>
                                    <mat-error *ngIf="form.get('name')?.hasError('required')">Policy name is required</mat-error>
                                    <mat-error *ngIf="form.get('name')?.hasError('minlength')">Name must be at least 3 characters</mat-error>
                                </mat-form-field>

                                <mat-form-field appearance="outline" class="flex-1">
                                    <mat-label>Category</mat-label>
                                    <mat-select formControlName="category">
                                        <mat-option *ngFor="let cat of policyCategories" [value]="cat.value">
                                            <mat-icon>{{ cat.icon }}</mat-icon>
                                            {{ cat.label }}
                                        </mat-option>
                                    </mat-select>
                                    <mat-icon matSuffix>{{ getCategoryIcon(form.get('category')?.value) }}</mat-icon>
                                </mat-form-field>
                            </div>

                            <!-- Custom category field (shows when 'custom' is selected) -->
                            <div class="form-row" *ngIf="isCustomCategory">
                                <mat-form-field appearance="outline" class="flex-1">
                                    <mat-label>Custom Category Name</mat-label>
                                    <input matInput type="text" formControlName="customCategory" placeholder="Enter custom category" />
                                    <mat-icon matSuffix>category</mat-icon>
                                </mat-form-field>
                            </div>

                            <div class="form-row">
                                <mat-form-field appearance="outline" class="flex-1">
                                    <mat-label>Description (Optional)</mat-label>
                                    <textarea matInput formControlName="description" rows="2" 
                                        placeholder="Brief summary of what this policy covers..."></textarea>
                                    <mat-hint>A short summary for quick reference</mat-hint>
                                </mat-form-field>
                            </div>
                        </div>

                        <!-- === Section 2: Policy Content === -->
                        <div class="form-section">
                            <h3 class="section-title">
                                <mat-icon>article</mat-icon> Policy Content
                                <button mat-icon-button type="button" 
                                        (click)="openAiAssistance()" 
                                        matTooltip="Get AI assistance with policy writing"
                                        class="ai-assist-btn">
                                    <mat-icon>auto_awesome</mat-icon>
                                </button>
                            </h3>
                            
                            <div class="form-row">
                                <div class="tinymce-container">
                                    <!-- Only render TinyMCE when edit tab is active to ensure proper initialization -->
                                    <editor *ngIf="tabIndex === 0"
                                        [init]="tinymceConfig"
                                        [disabled]="false"
                                        (onInit)="onEditorInit($event)"
                                        formControlName="policyWriting">
                                    </editor>
                                    <mat-hint class="editor-hint">The complete policy text. Click the AI button for assistance.</mat-hint>
                                    <mat-error *ngIf="form.get('policyWriting')?.hasError('required') && form.get('policyWriting')?.touched">Policy content is required</mat-error>
                                    <mat-error *ngIf="form.get('policyWriting')?.hasError('minlength') && form.get('policyWriting')?.touched">Policy must be at least 20 characters</mat-error>
                                </div>
                            </div>
                        </div>

                        <!-- === Section 3: Dates === -->
                        <div class="form-section">
                            <h3 class="section-title">
                                <mat-icon>event</mat-icon> Dates & Scheduling
                            </h3>
                            
                            <!-- Timepicker components (hidden, referenced by inputs) -->
                            <ngx-mat-timepicker #effectiveTimePicker></ngx-mat-timepicker>
                            <ngx-mat-timepicker #publishTimePicker></ngx-mat-timepicker>
                            <ngx-mat-timepicker #expiryTimePicker></ngx-mat-timepicker>
                            <ngx-mat-timepicker #deadlineTimePicker></ngx-mat-timepicker>
                            
                            <div class="form-row">
                                <div class="datetime-group flex-1">
                                    <mat-form-field appearance="outline" class="date-field">
                                        <mat-label>Effective Date</mat-label>
                                        <input matInput [matDatepicker]="effectivePicker" formControlName="effectiveDate">
                                        <mat-datepicker-toggle matSuffix [for]="effectivePicker"></mat-datepicker-toggle>
                                        <mat-datepicker #effectivePicker></mat-datepicker>
                                    </mat-form-field>
                                    <mat-form-field appearance="outline" class="time-field">
                                        <mat-label>Time</mat-label>
                                        <input matInput [ngxMatTimepicker]="effectiveTimePicker" formControlName="effectiveTime" placeholder="--:--">
                                        <mat-icon matSuffix class="time-icon">schedule</mat-icon>
                                    </mat-form-field>
                                    <mat-hint class="datetime-hint">When the policy becomes effective</mat-hint>
                                </div>

                                <div class="datetime-group flex-1">
                                    <mat-form-field appearance="outline" class="date-field">
                                        <mat-label>Publish Date (Scheduled)</mat-label>
                                        <input matInput [matDatepicker]="publishPicker" formControlName="publishDate">
                                        <mat-datepicker-toggle matSuffix [for]="publishPicker"></mat-datepicker-toggle>
                                        <mat-datepicker #publishPicker></mat-datepicker>
                                    </mat-form-field>
                                    <mat-form-field appearance="outline" class="time-field">
                                        <mat-label>Time</mat-label>
                                        <input matInput [ngxMatTimepicker]="publishTimePicker" formControlName="publishTime" placeholder="--:--">
                                        <mat-icon matSuffix class="time-icon">schedule</mat-icon>
                                    </mat-form-field>
                                    <mat-hint class="datetime-hint">Leave empty to publish immediately after approval</mat-hint>
                                </div>
                            </div>

                            <div class="form-row">
                                <div class="datetime-group flex-1">
                                    <mat-form-field appearance="outline" class="date-field">
                                        <mat-label>Expiry Date (Optional)</mat-label>
                                        <input matInput [matDatepicker]="expiryPicker" formControlName="expiryDate">
                                        <mat-datepicker-toggle matSuffix [for]="expiryPicker"></mat-datepicker-toggle>
                                        <mat-datepicker #expiryPicker></mat-datepicker>
                                    </mat-form-field>
                                    <mat-form-field appearance="outline" class="time-field">
                                        <mat-label>Time</mat-label>
                                        <input matInput [ngxMatTimepicker]="expiryTimePicker" formControlName="expiryTime" placeholder="--:--">
                                        <mat-icon matSuffix class="time-icon">schedule</mat-icon>
                                    </mat-form-field>
                                    <mat-hint class="datetime-hint">When the policy expires (leave empty for no expiry)</mat-hint>
                                </div>
                            </div>
                        </div>

                        <!-- === Section 4: Acknowledgment Settings === -->
                        <div class="form-section">
                            <h3 class="section-title">
                                <mat-icon>fact_check</mat-icon> Acknowledgment Settings
                            </h3>
                            
                            <div class="form-row checkbox-row">
                                <mat-checkbox formControlName="requiresAcknowledgment" color="primary">
                                    <span class="checkbox-label">
                                        <strong>Requires Acknowledgment</strong>
                                        <span class="checkbox-hint">Users must confirm they have read and understood this policy</span>
                                    </span>
                                </mat-checkbox>
                            </div>

                            <div class="form-row checkbox-row" *ngIf="showAcknowledgmentOptions">
                                <mat-checkbox formControlName="isMandatory" color="warn">
                                    <span class="checkbox-label">
                                        <strong>Mandatory Policy</strong>
                                        <span class="checkbox-hint">Users cannot dismiss - must acknowledge to continue</span>
                                    </span>
                                </mat-checkbox>
                            </div>

                            <div class="form-row" *ngIf="showAcknowledgmentOptions">
                                <div class="datetime-group flex-1">
                                    <mat-form-field appearance="outline" class="date-field">
                                        <mat-label>Acknowledgment Deadline</mat-label>
                                        <input matInput [matDatepicker]="deadlinePicker" formControlName="acknowledgmentDeadline">
                                        <mat-datepicker-toggle matSuffix [for]="deadlinePicker"></mat-datepicker-toggle>
                                        <mat-datepicker #deadlinePicker></mat-datepicker>
                                    </mat-form-field>
                                    <mat-form-field appearance="outline" class="time-field">
                                        <mat-label>Time</mat-label>
                                        <input matInput [ngxMatTimepicker]="deadlineTimePicker" formControlName="acknowledgmentDeadlineTime" placeholder="--:--">
                                        <mat-icon matSuffix class="time-icon">schedule</mat-icon>
                                    </mat-form-field>
                                    <mat-hint class="datetime-hint">Deadline for users to acknowledge this policy</mat-hint>
                                </div>
                            </div>
                        </div>

                        <!-- === Section 5: Target Selection === -->
                        <div class="form-section">
                            <h3 class="section-title">
                                <mat-icon>groups</mat-icon> Target Audience
                            </h3>
                            
                            <div class="form-row">
                                <mat-radio-group formControlName="targetMode" class="target-mode-group">
                                    <mat-radio-button value="all_organization" color="primary">
                                        <span class="radio-label">
                                            <mat-icon>business</mat-icon>
                                            <span>
                                                <strong>All Organization</strong>
                                                <span class="radio-hint">Policy applies to everyone in the organization</span>
                                            </span>
                                        </span>
                                    </mat-radio-button>
                                    <mat-radio-button value="selected" color="primary">
                                        <span class="radio-label">
                                            <mat-icon>checklist</mat-icon>
                                            <span>
                                                <strong>Selected Targets</strong>
                                                <span class="radio-hint">Choose specific parts, hats, or users</span>
                                            </span>
                                        </span>
                                    </mat-radio-button>
                                </mat-radio-group>
                            </div>

                            <!-- Target Selection Button and Summary (shows when 'selected' mode is chosen) -->
                            <div class="form-row target-selection-row" *ngIf="form.get('targetMode')?.value === 'selected'">
                                <button mat-stroked-button type="button" color="primary" 
                                        (click)="openTargetSelection()"
                                        [disabled]="shouldLockFields">
                                    <mat-icon>{{ selectedTargets ? 'edit' : 'add' }}</mat-icon>
                                    {{ selectedTargets ? 'Edit Targets' : 'Select Targets' }}
                                </button>
                                
                                <!-- Selected Targets Summary -->
                                <div class="targets-summary" *ngIf="selectedTargets">
                                    <div class="summary-item">
                                        <mat-icon>account_tree</mat-icon>
                                        <span class="count">{{ targetSummary.partCount }}</span>
                                        <span class="label">Parts</span>
                                    </div>
                                    <div class="summary-item">
                                        <mat-icon>badge</mat-icon>
                                        <span class="count">{{ targetSummary.hatCount }}</span>
                                        <span class="label">Hats</span>
                                    </div>
                                    <div class="summary-item">
                                        <mat-icon>people</mat-icon>
                                        <span class="count">{{ targetSummary.userCount }}</span>
                                        <span class="label">Staff</span>
                                    </div>
                                </div>
                                
                                <!-- Empty state when no targets selected -->
                                <div class="empty-targets" *ngIf="!selectedTargets">
                                    <mat-icon>info</mat-icon>
                                    <span>No targets selected yet. Click to select.</span>
                                </div>
                            </div>
                        </div>

                        <!-- === Section 6: Approval Workflow === -->
                        <div class="form-section">
                            <h3 class="section-title">
                                <mat-icon>approval</mat-icon> Approval Workflow
                            </h3>
                            
                            <div class="form-row approval-row">
                                <button mat-stroked-button type="button" color="primary" 
                                        (click)="openApproversSelection()"
                                        [disabled]="shouldLockFields">
                                    <mat-icon>{{ approvalWorkflow ? 'edit' : 'person_add' }}</mat-icon>
                                    {{ approvalWorkflow ? 'Edit Approvers' : 'Configure Approvers' }}
                                </button>
                                
                                <!-- Approval Summary -->
                                <div class="approval-summary" *ngIf="approvalWorkflow && approvalStepCount > 0">
                                    <div class="approval-steps-preview">
                                        <div class="step-badge" *ngFor="let step of approvalWorkflow.steps; let i = index">
                                            <span class="step-number">{{ i + 1 }}</span>
                                            <span class="step-name">{{ step.name }}</span>
                                            <mat-icon class="connector" *ngIf="i < approvalWorkflow.steps.length - 1">arrow_forward</mat-icon>
                                        </div>
                                    </div>
                                </div>
                                
                                <!-- No Approval Message -->
                                <div class="no-approval" *ngIf="!approvalWorkflow || approvalStepCount === 0">
                                    <mat-icon>info</mat-icon>
                                    <span>No approval required - can publish directly</span>
                                </div>
                            </div>
                        </div>

                    </form>
                </div>
            </div>

        </mat-tab>


        <!-- Existing Policies Tab -->
        <mat-tab label="Existing Policies">

            <div class="view-container m-3">
                <div class="view-header">
                    <span class="viewer-title-spacer mb-3">Organization Policies <span class="policy-total-count">{{ getStatusCount(null) }}</span></span>

                    <mat-form-field class="mw-300" appearance="outline">
                        <mat-label>Search</mat-label>
                        <input matInput (keyup)="applyFilter($event)" placeholder="Search by name, hat, staff member..." />
                        <mat-icon matSuffix>search</mat-icon>
                    </mat-form-field>
                </div>

                <!-- Status Filter Chips -->
                <div class="status-filters">
                    <button mat-stroked-button 
                            class="filter-chip" 
                            [class.active]="!activeStatusFilter"
                            (click)="filterByStatus(null)">
                        <mat-icon>filter_list</mat-icon>
                        All
                        <span class="chip-count">{{ getStatusCount(null) }}</span>
                    </button>
                    <button mat-stroked-button 
                            class="filter-chip status-draft" 
                            [class.active]="activeStatusFilter === 'draft'"
                            (click)="filterByStatus('draft')">
                        <mat-icon>edit</mat-icon>
                        Drafts
                        <span class="chip-count">{{ getStatusCount('draft') }}</span>
                    </button>
                    <button mat-stroked-button 
                            class="filter-chip status-pending_approval" 
                            [class.active]="activeStatusFilter === 'pending_approval'"
                            (click)="filterByStatus('pending_approval')">
                        <mat-icon>hourglass_empty</mat-icon>
                        Pending
                        <span class="chip-count">{{ getStatusCount('pending_approval') }}</span>
                    </button>
                    <button mat-stroked-button 
                            class="filter-chip status-approved" 
                            [class.active]="activeStatusFilter === 'approved'"
                            (click)="filterByStatus('approved')">
                        <mat-icon>check_circle</mat-icon>
                        Approved
                        <span class="chip-count">{{ getStatusCount('approved') }}</span>
                    </button>
                    <button mat-stroked-button 
                            class="filter-chip status-published" 
                            [class.active]="activeStatusFilter === 'published'"
                            (click)="filterByStatus('published')">
                        <mat-icon>publish</mat-icon>
                        Published
                        <span class="chip-count">{{ getStatusCount('published') }}</span>
                    </button>
                    <button mat-stroked-button 
                            class="filter-chip status-expired" 
                            [class.active]="activeStatusFilter === 'expired'"
                            (click)="filterByStatus('expired')">
                        <mat-icon>event_busy</mat-icon>
                        Expired
                        <span class="chip-count">{{ getStatusCount('expired') }}</span>
                    </button>
                    <button mat-stroked-button 
                            class="filter-chip status-archived" 
                            [class.active]="activeStatusFilter === 'archived'"
                            (click)="filterByStatus('archived')">
                        <mat-icon>archive</mat-icon>
                        Archived
                        <span class="chip-count">{{ getStatusCount('archived') }}</span>
                    </button>
                    <button mat-stroked-button 
                            class="filter-chip status-scheduled" 
                            [class.active]="activeStatusFilter === 'scheduled'"
                            (click)="filterByStatus('scheduled')">
                        <mat-icon>schedule_send</mat-icon>
                        Scheduled
                        <span class="chip-count">{{ getStatusCount('scheduled') }}</span>
                    </button>
                    <button mat-stroked-button 
                            class="filter-chip status-cancelled" 
                            [class.active]="activeStatusFilter === 'cancelled'"
                            (click)="filterByStatus('cancelled')">
                        <mat-icon>cancel</mat-icon>
                        Cancelled
                        <span class="chip-count">{{ getStatusCount('cancelled') }}</span>
                    </button>
                    <button mat-stroked-button 
                            class="filter-chip status-revoked" 
                            [class.active]="activeStatusFilter === 'revoked'"
                            (click)="filterByStatus('revoked')">
                        <mat-icon>block</mat-icon>
                        Revoked
                        <span class="chip-count">{{ getStatusCount('revoked') }}</span>
                    </button>
                    <button mat-stroked-button 
                            class="filter-chip status-deleted" 
                            [class.active]="activeStatusFilter === 'deleted'"
                            (click)="filterByStatus('deleted')">
                        <mat-icon>delete</mat-icon>
                        Deleted
                        <span class="chip-count">{{ getStatusCount('deleted') }}</span>
                    </button>
                </div>
                
                <!-- Policy Cards List -->
                <div class="policies-list" *ngIf="dataSource.filteredData.length > 0">
                    <div class="policy-card" *ngFor="let policy of dataSource.filteredData" (dblclick)="editPolicy(policy)">
                        
                        <!-- Card Main Content -->
                        <div class="card-main">
                            <!-- Row 1: Title (full width) -->
                            <h3 class="policy-name" matTooltip="Policy name">{{ policy.name }}</h3>
                            
                            <!-- Row 2: Columns -->
                            <div class="card-columns">
                                <!-- Column 1: Meta + Status -->
                                <div class="card-left-col">
                                    <div class="card-meta-row">
                                        <div class="policy-category" matTooltip="Category">
                                            <mat-icon>{{ getCategoryIcon(policy.category || 'general') }}</mat-icon>
                                            <span>{{ getCategoryLabel(policy.category || 'general') }}</span>
                                        </div>
                                        <div class="policy-creator" *ngIf="policy.createdBy" matTooltip="Created by">
                                            <mat-icon>person</mat-icon>
                                            <span>{{ getCreatorName(policy.createdBy) }}</span>
                                        </div>
                                    </div>

                                    <div class="status-row">
                                        <span class="status-badge status-{{ policy.status || 'draft' }}" [matTooltip]="'Status: ' + getStatusConfig(policy.status || 'draft').label">
                                            <mat-icon>{{ getStatusConfig(policy.status || 'draft').icon }}</mat-icon>
                                            {{ getStatusConfig(policy.status || 'draft').label }}
                                        </span>
                                        <div class="publish-info" *ngIf="policy.publishDate" matTooltip="Scheduled publish date & time">
                                            <mat-icon>event</mat-icon>
                                            <span>{{ $any(policy.publishDate).toDate ? ($any(policy.publishDate).toDate() | date:'short') : '-' }}</span>
                                        </div>
                                    </div>
                                </div>
                                
                                <!-- Column 2: Dates -->
                                <div class="card-dates-col">
                                    <div class="date-item" *ngIf="policy.createdOn" matTooltip="Created date" matTooltipPosition="below">
                                        <mat-icon>add_circle_outline</mat-icon>
                                        <div class="date-info">
                                            <span class="date-label">Created</span>
                                            <span class="date-value">{{ $any(policy.createdOn).toDate ? ($any(policy.createdOn).toDate() | date:'shortDate') : '-' }}</span>
                                        </div>
                                    </div>
                                    <div class="date-item" *ngIf="policy.modifiedOn" matTooltip="Last modified date" matTooltipPosition="below">
                                        <mat-icon>update</mat-icon>
                                        <div class="date-info">
                                            <span class="date-label">Modified</span>
                                            <span class="date-value">{{ $any(policy.modifiedOn).toDate ? ($any(policy.modifiedOn).toDate() | date:'shortDate') : '-' }}</span>
                                        </div>
                                    </div>
                                    <div class="date-item effective" *ngIf="policy.effectiveDate" matTooltip="Effective date & time" matTooltipPosition="below">
                                        <mat-icon>play_arrow</mat-icon>
                                        <div class="date-info">
                                            <span class="date-label">Effective</span>
                                            <span class="date-value">{{ $any(policy.effectiveDate).toDate ? ($any(policy.effectiveDate).toDate() | date:'short') : '-' }}</span>
                                        </div>
                                    </div>
                                    <div class="date-item expiry" *ngIf="policy.expiryDate" matTooltip="Expiry date & time" matTooltipPosition="below">
                                        <mat-icon>event_busy</mat-icon>
                                        <div class="date-info">
                                            <span class="date-label">Expires</span>
                                            <span class="date-value">{{ $any(policy.expiryDate).toDate ? ($any(policy.expiryDate).toDate() | date:'short') : '-' }}</span>
                                        </div>
                                    </div>
                                </div>
                                
                                <!-- Column 3: Badges (flowing, wrapping) -->
                                <div class="card-badges-flow">
                                    <!-- All Org badge -->
                                    <div class="badge" *ngIf="policy.targets?.mode === 'all_organization'" matTooltip="Applies to everyone in the organization">
                                        <mat-icon>corporate_fare</mat-icon>
                                        <span>All Org</span>
                                    </div>
                                    <!-- Selected targets: individual counts -->
                                    <div class="badge" *ngIf="policy.targets?.mode === 'selected' && policy.targets?.parts?.length" [matTooltip]="(policy.targets?.parts?.length || 0) + ' organization parts targeted'">
                                        <mat-icon>account_tree</mat-icon>
                                        <span>{{ policy.targets?.parts?.length || 0 }}</span>
                                    </div>
                                    <div class="badge" *ngIf="policy.targets?.mode === 'selected' && policy.targets?.directHats?.length" [matTooltip]="(policy.targets?.directHats?.length || 0) + ' positions targeted'">
                                        <mat-icon>badge</mat-icon>
                                        <span>{{ policy.targets?.directHats?.length || 0 }}</span>
                                    </div>
                                    <div class="badge" *ngIf="policy.targets?.mode === 'selected' && policy.targets?.directUsers?.length" [matTooltip]="(policy.targets?.directUsers?.length || 0) + ' users targeted'">
                                        <mat-icon>people</mat-icon>
                                        <span>{{ policy.targets?.directUsers?.length || 0 }}</span>
                                    </div>
                                    <!-- Instated count (published policies) -->
                                    <div class="badge instated" *ngIf="policy.status === 'published' && getResolvedHatCount(policy) > 0"
                                         [matTooltip]="getResolvedHatCount(policy) + ' position' + (getResolvedHatCount(policy) !== 1 ? 's' : '') + ' assigned'">
                                        <mat-icon>assignment_ind</mat-icon>
                                        <span>{{ getResolvedHatCount(policy) }}</span>
                                    </div>
                                    <!-- Version -->
                                    <span class="badge version" [matTooltip]="'Version ' + (policy.version || 1)">v{{ policy.version || 1 }}</span>
                                    <!-- Revisions -->
                                    <span class="badge revision" *ngIf="policy.revisions?.length" 
                                          [matTooltip]="(policy.revisions?.length || 0) + ' revision' + ((policy.revisions?.length || 0) !== 1 ? 's' : '')">
                                        <mat-icon>history</mat-icon>{{ policy.revisions?.length || 0 }}
                                    </span>
                                    <!-- Approval workflow status (only if workflow exists) -->
                                    <ng-container *ngIf="getApprovalInfo(policy) as approval">
                                        <div class="badge approval" *ngIf="approval.hasWorkflow" 
                                             [class.all-approved]="approval.approvedSteps === approval.totalSteps"
                                             [class.has-rejected]="approval.rejectedSteps > 0"
                                             [class.pending]="approval.pendingSteps > 0 && approval.rejectedSteps === 0"
                                             [matTooltip]="approval.approvedSteps + ' of ' + approval.totalSteps + ' approved' + (approval.rejectedSteps > 0 ? ', ' + approval.rejectedSteps + ' rejected' : '')">
                                            <mat-icon>
                                                {{ approval.rejectedSteps > 0 ? 'cancel' : (approval.approvedSteps === approval.totalSteps ? 'verified' : 'how_to_reg') }}
                                            </mat-icon>
                                            <span>{{ approval.approvedSteps }}/{{ approval.totalSteps }}</span>
                                        </div>
                                    </ng-container>
                                    <!-- Acknowledged count (published + requiresAcknowledgment) -->
                                    <div class="badge acknowledged" 
                                         *ngIf="policy.status === 'published' && policy.requiresAcknowledgment && getResolvedHatCount(policy) > 0"
                                         [class.complete]="policy.acknowledgmentStats && (policy.acknowledgmentStats.totalAcknowledged || 0) >= (policy.acknowledgmentStats.totalRequired || 1)"
                                         [class.partial]="policy.acknowledgmentStats && (policy.acknowledgmentStats.totalAcknowledged || 0) > 0 && (policy.acknowledgmentStats.totalAcknowledged || 0) < (policy.acknowledgmentStats.totalRequired || 1)"
                                         [matTooltip]="policy.acknowledgmentStats ? ((policy.acknowledgmentStats.totalAcknowledged || 0) + ' of ' + policy.acknowledgmentStats.totalRequired + ' users acknowledged') : 'Acknowledgment required - stats pending'">
                                        <mat-icon>{{ policy.acknowledgmentStats && (policy.acknowledgmentStats.totalAcknowledged || 0) >= (policy.acknowledgmentStats.totalRequired || 1) ? 'check_circle' : 'pending' }}</mat-icon>
                                        <span *ngIf="policy.acknowledgmentStats">{{ policy.acknowledgmentStats.totalAcknowledged || 0 }}/{{ policy.acknowledgmentStats.totalRequired }}</span>
                                        <span *ngIf="!policy.acknowledgmentStats">0/?</span>
                                    </div>
                                    <!-- Processing spinner (shows during actions) -->
                                    <mat-spinner *ngIf="processingPolicyKey === policy.key" diameter="16" class="processing-spinner-badge"></mat-spinner>
                                </div>
                            </div>
                        </div>

                        <!-- Actions (vertically centered on whole card) -->
                        <div class="card-actions">
                            <button mat-icon-button class="card-action-btn" [matMenuTriggerFor]="rowMenu" 
                                    (click)="$event.stopPropagation()" matTooltip="Actions"
                                    [disabled]="processingPolicyKey === policy.key">
                                <mat-icon>more_vert</mat-icon>
                            </button>
                            <button mat-icon-button class="card-action-btn" 
                                    (click)="viewPolicyDetails(policy); $event.stopPropagation()" 
                                    matTooltip="View Details">
                                <mat-icon>visibility</mat-icon>
                            </button>
                            <button mat-icon-button class="card-action-btn" 
                                    (click)="editPolicy(policy); $event.stopPropagation()" 
                                    *ngIf="policy.status !== 'deleted'"
                                    [disabled]="processingPolicyKey === policy.key"
                                    matTooltip="Edit Policy">
                                <mat-icon>edit</mat-icon>
                            </button>
                            <mat-menu #rowMenu="matMenu">
                                <!-- EDIT - Available for most statuses (not deleted) -->
                                <button mat-menu-item (click)="editPolicy(policy)" 
                                        *ngIf="policy.status !== 'deleted'">
                                    <mat-icon>edit</mat-icon> Edit
                                </button>
                                    
                                    <!-- VIEW - Always available -->
                                    <button mat-menu-item (click)="viewPolicyDetails(policy)">
                                        <mat-icon>visibility</mat-icon> View Details
                                    </button>
                                    
                                    <mat-divider></mat-divider>
                                    
                                    <!-- APPROVE - For pending_approval with direct approval permission -->
                                    <button mat-menu-item (click)="approvePolicy(policy)" 
                                            *ngIf="canApproveDirectly(policy)">
                                        <mat-icon class="approve-icon">check_circle</mat-icon> Approve
                                    </button>
                                    
                                    <!-- PUBLISH NOW - For approved or scheduled status -->
                                    <button mat-menu-item (click)="publishPolicyFromList(policy, $event)" 
                                            *ngIf="policy.status === 'approved' || policy.status === 'scheduled'">
                                        <mat-icon color="primary">publish</mat-icon> Publish Now
                                    </button>
                                    
                                    <!-- CANCEL - For approved or scheduled (before publication) -->
                                    <button mat-menu-item (click)="cancelPolicy(policy, $event)" 
                                            *ngIf="policy.status === 'approved' || policy.status === 'scheduled'">
                                        <mat-icon color="warn">cancel</mat-icon> Cancel
                                    </button>
                                    
                                    <!-- REPUBLISH - Only for expired status -->
                                    <button mat-menu-item (click)="republishPolicyFromList(policy, $event)" 
                                            *ngIf="policy.status === 'expired'">
                                        <mat-icon color="primary">replay</mat-icon> Republish
                                    </button>
                                    
                                    <!-- REVOKE - Only for published status -->
                                    <button mat-menu-item (click)="revokePolicy(policy, $event)" 
                                            *ngIf="policy.status === 'published'" class="warn-action">
                                        <mat-icon color="warn">block</mat-icon> Revoke
                                    </button>
                                    
                                    <!-- ARCHIVE - For published, expired, cancelled, revoked -->
                                    <button mat-menu-item (click)="archivePolicy(policy, $event)" 
                                            *ngIf="['published', 'expired', 'cancelled', 'revoked'].includes(policy.status)">
                                        <mat-icon>inventory_2</mat-icon> Archive
                                    </button>
                                    
                                    <mat-divider *ngIf="['deleted', 'cancelled', 'revoked'].includes(policy.status)"></mat-divider>
                                    
                                    <!-- REINSTATE - For deleted, cancelled, revoked -->
                                    <button mat-menu-item (click)="reinstatePolicy(policy, $event)" 
                                            *ngIf="['deleted', 'cancelled', 'revoked'].includes(policy.status)" class="reinstate-action">
                                        <mat-icon color="primary">restore</mat-icon> Reinstate to Draft
                                    </button>
                                    
                                    <!-- CREATE NEW VERSION - For deleted, cancelled, revoked -->
                                    <button mat-menu-item (click)="createNewVersionFromList(policy, $event)" 
                                            *ngIf="['deleted', 'cancelled', 'revoked'].includes(policy.status)">
                                        <mat-icon color="primary">add_circle</mat-icon> Create New Version
                                    </button>
                                    
                                    <mat-divider *ngIf="policy.status !== 'deleted'"></mat-divider>
                                    
                                    <!-- DELETE - Available for all except already deleted -->
                                    <button mat-menu-item (click)="deletePolicy(policy, $event)" 
                                            *ngIf="policy.status !== 'deleted'" class="delete-action">
                                        <mat-icon color="warn">delete</mat-icon> Delete
                                    </button>
                                </mat-menu>
                        </div>
                    </div>
                </div>

                <!-- Empty state -->
                <div class="empty-state" *ngIf="dataSource.filteredData.length === 0">
                    <mat-icon>policy</mat-icon>
                    <p>No policies found</p>
                    <button mat-raised-button color="primary" (click)="tabIndex = 0; resetForm()">
                        <mat-icon>add</mat-icon> Create First Policy
                    </button>
                </div>
            </div>


        </mat-tab>
    </mat-tab-group>




</mat-dialog-content>

<mat-dialog-actions align="end">
    <button mat-button mat-dialog-close>Close</button>
    <!-- Normal save for non-deleted policies (only show on Edit tab, not list view) -->
    <button mat-stroked-button (click)="savePolicy()" color="primary" 
            [disabled]="form.invalid || form.pristine"
            *ngIf="!isEditingDeletedPolicy && tabIndex === 0">
        <mat-icon>save</mat-icon> Save Draft
    </button>
    <!-- Reinstate & Save for deleted policies (only show on Edit tab) -->
    <button mat-stroked-button (click)="reinstateAndSave()" color="primary" 
            [disabled]="form.invalid"
            *ngIf="isEditingDeletedPolicy && tabIndex === 0">
        <mat-icon>restore</mat-icon> Reinstate & Save
    </button>
</mat-dialog-actions>